import type { AutomergeUrl } from "@automerge/automerge-repo";
import { useDocHandle, useDocument } from "@automerge/react";
import type { CommentAgent } from "forest/comments/CommentComposer";
import {
  CommentThread,
  type ThreadMessage,
} from "forest/comments/CommentThread";
import {
  addTextAnnotation,
  replyToAnnotation,
  setAnnotationResolved,
  textAnnotations,
  type Message,
  type PositionedTextAnnotation,
} from "playground/annotations/annotations";
import type { TextDoc } from "playground/fs/types";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { useAskBear } from "@/comments/ask-bear";
import { useSignedInUser, type SessionUser } from "@/lib/session";

export const BEAR: CommentAgent = { mention: "agent:bear", name: "Bear" };

/** A selection about to become a thread. */
export interface TextCommentDraft {
  start: number;
  end: number;
  quote: string;
  /** How the selection reads in the thread's excerpt. */
  display: string;
}

export interface TextHighlight {
  id: string;
  range: { start: number; end: number };
}

const DRAFT = "draft";

/** Messages as a thread card shows them. */
export function threadMessageViews(
  messages: Message[],
  user: SessionUser,
): ThreadMessage[] {
  return messages.map((message) => ({
    id: message.id,
    // The earliest text comments were stored before names were.
    authorName:
      message.authorName ?? (message.author === user.id ? user.name : "Member"),
    agent: message.author.startsWith("agent:"),
    body: message.body,
    createdAt: message.createdAt,
  }));
}

function threadMessages(
  messages: Record<string, Message>,
  user: SessionUser,
): ThreadMessage[] {
  return threadMessageViews(
    Object.values(messages).sort((a, b) => a.createdAt - b.createdAt),
    user,
  );
}

/**
 * The comment threads on a text file, for any surface that shows its text
 * (rendered Markdown, Monaco): what to highlight, which thread is open, and
 * that thread's card. Resolved threads stay listed but aren't highlighted.
 */
export function useTextComments(url: AutomergeUrl | undefined, path: string) {
  const [doc] = useDocument<TextDoc>(url);
  const handle = useDocHandle<TextDoc>(url);
  const user = useSignedInUser();
  const askBear = useAskBear();
  // Bear is offered only where an ask can reach a conversation.
  const agent = askBear ? BEAR : undefined;

  /** A post addressed to Bear sends the thread, as it now reads, to chat. */
  const ask = (id: string, mentions: string[]) => {
    const current = handle?.doc();
    const thread =
      current &&
      textAnnotations(current).find((item) => item.annotation.id === id);
    if (!askBear || !thread || !mentions.includes(BEAR.mention)) return;
    askBear({
      path,
      threadId: id,
      on: `"${thread.anchor.quote}"`,
      messages: threadMessages(thread.annotation.messages, user).map(
        ({ authorName, body }) => ({ authorName, body }),
      ),
    }).catch((error: unknown) =>
      toast.error(
        error instanceof Error ? error.message : "Could not ask Bear",
      ),
    );
  };
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState<TextCommentDraft | null>(null);
  const threads = doc ? textAnnotations(doc) : [];

  const highlights: TextHighlight[] = [
    ...threads.flatMap(({ annotation, range }) =>
      range && (!annotation.resolved || annotation.id === activeId)
        ? [{ id: annotation.id, range }]
        : [],
    ),
    ...(draft
      ? [{ id: DRAFT, range: { start: draft.start, end: draft.end } }]
      : []),
  ];

  const close = () => {
    setActiveId(null);
    setDraft(null);
  };

  const create = (body: string, mentions: string[]) => {
    if (!handle || !draft) return;
    try {
      const id = addTextAnnotation(handle, draft, {
        author: user.id,
        authorName: user.name,
        body,
        mentions,
      });
      setDraft(null);
      setActiveId(id);
      ask(id, mentions);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Could not add comment",
      );
    }
  };

  const cardFor = (thread: PositionedTextAnnotation): ReactNode => (
    <CommentThread
      excerpt={thread.anchor.quote}
      outdated={thread.range ? undefined : "The quoted text has changed."}
      messages={threadMessages(thread.annotation.messages, user)}
      resolved={thread.annotation.resolved}
      agent={agent}
      onResolvedChange={(resolved) =>
        handle && setAnnotationResolved(handle, thread.annotation.id, resolved)
      }
      onPost={({ body, mentions }) => {
        if (!handle) return;
        replyToAnnotation(handle, thread.annotation.id, {
          author: user.id,
          authorName: user.name,
          body,
          mentions,
        });
        ask(thread.annotation.id, mentions);
      }}
      onClose={close}
    />
  );

  const active = threads.find((thread) => thread.annotation.id === activeId);
  const card =
    activeId === DRAFT && draft ? (
      <CommentThread
        excerpt={draft.display}
        messages={[]}
        resolved={false}
        agent={agent}
        onPost={({ body, mentions }) => create(body, mentions)}
        onClose={close}
      />
    ) : active ? (
      cardFor(active)
    ) : null;

  return {
    /** The text the ranges index into, once the doc has loaded. */
    source: doc?.text,
    threads,
    highlights,
    activeId,
    /** The open thread's card. Surfaces anchor it to the thread's range. */
    card,
    /** The open thread when its text is gone, so it can't be anchored. */
    unanchored: active && !active.range ? cardFor(active) : null,
    activate: (id: string | null) => {
      if (id !== DRAFT) setDraft(null);
      setActiveId(id);
    },
    startDraft: (selection: TextCommentDraft) => {
      setDraft(selection);
      setActiveId(DRAFT);
    },
  };
}
